import { useId } from 'react';

/** Props for {@link TextArea}. */
export interface TextAreaProps
  extends Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, 'className' | 'style' | 'id'> {
  /** Visible label. Never omitted. */
  readonly label: string;
  /** Validation message. Presence switches the control into its error state. */
  readonly error?: string;
  /** Guidance shown below the control. */
  readonly hint?: string;
}

/**
 * A labelled multi-line text input.
 *
 * @param props - Component props.
 * @returns The textarea element.
 */
export function TextArea({ label, error, hint, ...rest }: TextAreaProps): React.JSX.Element {
  const id = useId();
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;

  const describedBy = [hint === undefined ? null : hintId, error === undefined ? null : errorId]
    .filter((value): value is string => value !== null)
    .join(' ');

  return (
    <div>
      <label className="gm-field-label" htmlFor={id}>
        {label}
      </label>
      <textarea
        {...rest}
        id={id}
        className="gm-textarea"
        aria-invalid={error === undefined ? undefined : true}
        aria-describedby={describedBy === '' ? undefined : describedBy}
      />
      {hint !== undefined && (
        <p id={hintId} className="gm-text-muted" style={{ margin: '4px 0 0', fontSize: '14px' }}>
          {hint}
        </p>
      )}
      {error !== undefined && (
        <p id={errorId} className="gm-field-error" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
